import { IModel, Model, ModelRecord, SalesSubCategoryModel, SpeciesModel, PhotoModel, MarketModel, SalesPackageContentModel, SalesPackagePriceModel, SalesPackagePriceRuleModel, ReportingGroupModel, BusinessLineModel, VisitSalesItemPublicModel, InvoiceSalesItemModel } from "@common/models";

export interface ISalesPackageModel extends IModel {
    salesPackageId: number;
    salesSubCategoryId: number;
    speciesId: number | undefined;
    name: string;
    displayName: string;
    description: string;
    code: string;
    helpPhotoId: number;
    sortOrder: number;
    expirationDays: number;
    vaccineReminderDefaultDays: number;
    businessLineId: number;
    businessLine: BusinessLineModel;
    salesSubCategory: SalesSubCategoryModel;
    species: SpeciesModel;
    helpPhoto: PhotoModel;
    markets: MarketModel[];
    reportingGroups: ReportingGroupModel[];
    contents: SalesPackageContentModel[];
    prices: SalesPackagePriceModel[];
    priceRules: SalesPackagePriceRuleModel[];
    color: string;
    clinicSKU: string;
    hospitalSKU: string;
    defaultStartDate: string;//UI Field Only
}

export class SalesPackageModel extends Model implements ModelRecord<ISalesPackageModel> {
    salesPackageId: number;
    salesSubCategoryId: number;
    speciesId: number | undefined;
    name: string;
    displayName: string;
    description: string;
    code: string;
    helpPhotoId: number;
    sortOrder: number;
    expirationDays: number;
    vaccineReminderDefaultDays: number;
    businessLineId: number;
    businessLine: BusinessLineModel;
    salesSubCategory: SalesSubCategoryModel;
    species: SpeciesModel;
    helpPhoto: PhotoModel;
    markets: MarketModel[];
    reportingGroups: ReportingGroupModel[];
    contents: SalesPackageContentModel[];
    prices: SalesPackagePriceModel[];
    priceRules: SalesPackagePriceRuleModel[];
    color: string;
    clinicSKU: string;
    hospitalSKU: string;
    defaultStartDate: string;//UI Field Only
    constructor(params?: Partial<ISalesPackageModel>) {
        super(params);
        this.salesPackageId = _.defaultTo(params?.salesPackageId, 0);
        this.salesSubCategoryId = _.defaultTo(params?.salesSubCategoryId, 0);
        this.speciesId = _.defaultTo(params?.speciesId, undefined);
        this.name = _.defaultTo(params?.name, '');
        this.displayName = _.defaultTo(params?.displayName, '');
        this.description = _.defaultTo(params?.description, '');
        this.code = _.defaultTo(params?.code, '');
        this.helpPhotoId = _.defaultTo(params?.helpPhotoId, 0);
        this.sortOrder = _.defaultTo(params?.sortOrder, 0);
        this.expirationDays = _.defaultTo(params?.expirationDays, 0);
        this.vaccineReminderDefaultDays = _.defaultTo(params?.vaccineReminderDefaultDays, 0);
        this.businessLineId = _.defaultTo(params?.businessLineId, 0);
        this.businessLine = params?.businessLine ? new BusinessLineModel(params.businessLine) : new BusinessLineModel();
        this.salesSubCategory = params?.salesSubCategory ? new SalesSubCategoryModel(params.salesSubCategory) : new SalesSubCategoryModel();
        this.species = params?.species ? new SpeciesModel(params.species) : new SpeciesModel();
        this.helpPhoto = params?.helpPhoto ? new PhotoModel(params.helpPhoto) : new PhotoModel();
        this.markets = (params?.markets ?? []).map(p => new MarketModel(p));
        this.reportingGroups = (params?.reportingGroups ?? []).map(p => new ReportingGroupModel(p));
        this.contents = (params?.contents ?? []).map(p => new SalesPackageContentModel(p));
        this.prices = (params?.prices ?? []).map(p => new SalesPackagePriceModel(p));
        this.priceRules = (params?.priceRules ?? []).map(p => new SalesPackagePriceRuleModel(p));
        this.color = _.defaultTo(params?.color, "#fff")
        this.clinicSKU = _.defaultTo(params?.clinicSKU, '');
        this.hospitalSKU = _.defaultTo(params?.hospitalSKU, '');
        this.defaultStartDate = _.defaultTo(params?.defaultStartDate, (new Date()).toISOString());

    }

    /**
    * Orders a set of visit/estimate sales items for presentation. Packaged items will appear first in their admin-defined sort order. Remaining items will appear last in the order they were added to the visit/estimate.
    * @param salesPackage The sales package applied to the visit/estimate (if any).
    * @param items The visit/estimate sales items.
    * @returns an ordered collection of visit/estimate sales items.
    */
    static OrderSalesItems<T extends { salesItemId: number, salesPackageId: number | undefined }>(items: T[], orderBy: string): T[] {
        // find all the visit sales items contained in the sales package (if there is one.) 
        const visitItemsInPackage = items.filter(vsi => vsi.salesPackageId !== undefined);
        // order the package contents by their admin-defined sort order...
        // const orderedPackageItems = _.orderBy(salesPackage?.contents, "sortOrder");
        // ... and subsequently order the visit package items by that same sort order.
        const orderedVisitItemsInPackage = _.orderBy(visitItemsInPackage, orderBy);
        // find all the visit sales items NOT contained in the sales package (if there is one)...
        const visitItemsNotInPackage = _.differenceBy(items, visitItemsInPackage, 'salesItemId');
        // ... and sort them by their visitSalesItemId. (This is a proxy for the order they were added to the visit.)
        const orderedVisitItemsNotInPackage = _.orderBy(visitItemsNotInPackage, orderBy);
        // concat the two lists with package items appearing first.
        return [...orderedVisitItemsInPackage, ...orderedVisitItemsNotInPackage];
    }

    /**
    * Orders a set of customer-visible visit sales items for presentation. Packaged items will appear first.
    * @param items The public visit sales items.
    * @returns sales package information (if any) and visit sales items.
    */
    static OrderPublicSalesItems(items: VisitSalesItemPublicModel[]): { packageName: string | undefined, packagePrice: number | undefined, items: VisitSalesItemPublicModel[] } {
        // find all the visit sales items contained in the sales package (if there is one.) 
        const visitItemsInPackage = items.filter(vsi => vsi.salesPackageName !== undefined);
        // find all the visit sales items NOT contained in the sales package (if there is one)...
        const visitItemsNotInPackage = items.filter(vsi => vsi.salesPackageName === undefined);
        return {
            packageName: _.isEmpty(visitItemsInPackage) ? undefined : visitItemsInPackage[0].salesPackageName,
            packagePrice: _.isEmpty(visitItemsInPackage) ? undefined : visitItemsInPackage[0].salesPackagePrice,
            items: [...visitItemsInPackage, ...visitItemsNotInPackage]
        }
    }

    static OrderInvoiceSalesItems(items: InvoiceSalesItemModel[]) {
        // find all the visit sales items contained in the sales package (if there is one.) 
        const visitItemsInPackage = items.filter(vsi => vsi.salesPackageId !== undefined);
        // ... and subsequently order the visit package items by that same sort order.
        const orderedVisitItemsInPackage = _.orderBy(visitItemsInPackage, 'invoiceSalesItemId');
        // find all the visit sales items NOT contained in the sales package (if there is one)...
        const visitItemsNotInPackage = _.differenceBy(items, visitItemsInPackage, 'invoiceSalesItemId');

        // Group and sort invoice items with their parents
        var orderedVisitItemsNotInPackage: InvoiceSalesItemModel[] = [];
        _(visitItemsNotInPackage)
            .groupBy(el => el.parentInvoiceSalesItemId)
            .forEach(element => {
                var parent = visitItemsNotInPackage.find(el => el.invoiceSalesItemId == element[0].parentInvoiceSalesItemId);
                if (parent) {
                    orderedVisitItemsNotInPackage.push(parent);
                }
                orderedVisitItemsNotInPackage = orderedVisitItemsNotInPackage.concat(_.differenceBy(element, orderedVisitItemsNotInPackage));
            });

        return [...orderedVisitItemsInPackage, ...orderedVisitItemsNotInPackage];
    }
}
